import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authenticateFromOauth, fetchPermissions, fetchPartners } from '@src/apis/reware';

const PERMISSIONS_PREFIX = 'config_portal_app_';
const PERMISSION_CODES = [
  'config_portal_app_plugins',
  'config_portal_app_catalog',
  'config_portal_app_developer_tools',
  'config_portal_app_repair'
];

const getAuthParamsFromUrl = (): { token: string | null, uid: string | null } => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    token: urlParams.get('token'),
    uid: urlParams.get('uid'),
  };
};

export const TroveCallback = () => {
  const notify = useNotify();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const { token, uid } = getAuthParamsFromUrl();

        if (!token || !uid) {
          throw new Error('Missing authentication tokens in callback URL');
        }

        const user = await authenticateFromOauth({
          token,
          userId: parseInt(uid, 10),
        });

        if (!user || !user.uuid) {
          throw new Error('Invalid authentication response from server');
        }

        sessionStorage.setItem('token', token);
        sessionStorage.setItem('uuid', user.uuid);
        if (user.name) sessionStorage.setItem('name', user.name);
        if (user.email) sessionStorage.setItem('email', user.email);

        window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: { uuid: user.uuid } }));

        const allPermissions = await fetchPermissions({ token });
        if (!allPermissions) {
          throw new Error('No user permissions found');
        }

        const filteredPermissions = Object.fromEntries(
          Object.entries(allPermissions).filter(([key]) =>
            key.startsWith(PERMISSIONS_PREFIX))
        );

        if (Object.keys(filteredPermissions).length === 0) {
          throw new Error('No permissions found for this application');
        }

        sessionStorage.setItem('permissions', JSON.stringify(filteredPermissions));

        const partners = await fetchPartners({
          token,
          permissionCodes: PERMISSION_CODES
        });

        sessionStorage.setItem('partners', JSON.stringify(partners));

        let redirectPath = '/select-partner';
        if (partners.length === 1) {
          redirectPath = '/';
          sessionStorage.setItem('selectedPartner', JSON.stringify(partners[0]));
        }

        setStatus('success');

        setTimeout(() => {
          window.location.replace(redirectPath);
        }, 500);

      } catch (error) {
        console.error('Trove callback processing failed:', error);
        const message = error instanceof Error ? error.message : 'Authentication failed';
        setErrorMessage(message);
        setStatus('error');
        notify(message, { type: 'error' });

        setTimeout(() => {
          window.location.replace('/login');
        }, 3000);
      }
    };

    void processCallback();
  }, [notify]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      {status === 'processing' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Completing authentication...
          </Typography>
        </Box>
      )}

      {status === 'success' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            ✓ Authentication successful
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to dashboard...
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error.main">
            ✗ Authentication failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to login...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
